import React from "react";
import Layout from "../component/layout/Layout";
import { Container, Paper, Box, Typography, Divider } from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  FileText,
  CheckCircle,
  ClipboardCheck,
  RefreshCcw,
  Ban,
  XCircle,
  Clock,
  Mail,
} from "lucide-react";



// ---------- styled components ----------
const PageWrap = styled("div")(({ theme }) => ({
  paddingTop: theme.spacing(6),
  paddingBottom: theme.spacing(8),
  background: "#fff",
}));

const Card = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: 12,
  boxShadow: "0 8px 30px rgba(7,32,58,0.06)",
  maxWidth: 1100,
  margin: "0 auto",
}));

const SectionHeader = styled("div")(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(2),
  alignItems: "center",
}));

const IconCircle = styled("div")(({ theme }) => ({
  width: 44,
  height: 44,
  borderRadius: 10,
  display: "grid",
  placeItems: "center",
  background: "linear-gradient(135deg, rgba(7,32,58,0.06), rgba(10,30,60,0.02))",
  color: "#07203a",
}));

const SectionBody = styled("div")(({ theme }) => ({
  marginTop: theme.spacing(1.5),
  color: "rgba(7,32,58,0.88)",
  lineHeight: 1.7,
}));

// icons mapped per section
const ICONS = [
  FileText,       // Introduction
  CheckCircle,    // Eligibility
  ClipboardCheck, // Conditions
  RefreshCcw,     // Processing
  Ban,            // Non refundable
  XCircle,        // Cancellations
  Clock,          // Changes policy
  Mail            // Contact
];

const RefundPolicy = () => {
  return (
    <Layout tab={2}>
      <PageWrap>
        <Container>
          <Card>
            <Box sx={{ textAlign: "center", mb: 1 }}>
              <Typography variant="h4" sx={{ fontWeight: 800 }}>
                Refund Policy
              </Typography>
              <Typography variant="subtitle2" sx={{ color: "rgba(7,32,58,0.6)", mt: 0.5 }}>
                Effective Date: 13/01/2025
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            {refundPolicyContent.map((section, index) => {
              const IconComponent = ICONS[index] ?? FileText;
              return (
                <Box key={index} sx={{ mb: 4 }}>
                  <SectionHeader>
                    <IconCircle>
                      <IconComponent size={20} />
                    </IconCircle>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: "#07203a" }}>
                      {section.title}
                    </Typography>
                  </SectionHeader>

                  <SectionBody>
                    <Typography variant="body2" component="p" sx={{ whiteSpace: "pre-line" }}>
                      {section.description}
                    </Typography>
                  </SectionBody>
                </Box>
              );
            })}
          </Card>
        </Container>
      </PageWrap>
    </Layout>
  );
};

export default RefundPolicy;





/* ================================================
   Original refund text — UNCHANGED (as requested)
   ================================================ */

const refundPolicyContent = [
  {
    title: "Introduction",
    description:
      "This Refund Policy is designed to ensure customer satisfaction and provide clarity regarding the refund process for our courses.",
  },
  {
    title: "Eligibility for Refunds",
    description:
      "Refund requests can be made within [X] days of purchasing the course. Refund eligibility depends on the specific circumstances outlined below.",
  },
  {
    title: "Conditions for Refunds",
    description:
      "Refund requests must be submitted via email or support form, including course details and reason for the refund. Refunds will only be processed for purchases made directly on the website. Refunds will not be granted if:\n- You have completed more than 50% of the course.\n- You have accessed downloadable content or completed assessments.",
  },
  {
    title: "Refund Processing",
    description:
      "If your refund request is approved, the amount will be credited back to the original payment method (via Razorpay or other payment gateways). The refund process can take [X] business days depending on the payment provider. Payment processing fees may not be refundable.",
  },
  {
    title: "Non-Refundable Items",
    description:
      "Any extra services (e.g., course bundles, coaching sessions) or products (e.g., physical materials) are non-refundable. Any discounts or promotional offers applied to the original purchase are non-refundable.",
  },
  {
    title: "Cancellations",
    description:
      "You can cancel your course access at any time by contacting our support team. If canceled within the eligibility period, you will be considered for a refund as per the conditions outlined above. Cancellations do not automatically imply a refund.",
  },
  {
    title: "Changes to the Refund Policy",
    description:
      "We reserve the right to modify or update the Refund Policy at any time. Please check this page periodically to stay informed about any changes.",
  },
  {
    title: "Contact Information",
    description:
      "For refund-related inquiries, please contact us:\nSupport Email: support@skillserve.in\nPhone: +91-7840010608",
  },
];
